import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import api from '../../lib/api';
import FactoringTransferSection from '../../components/FactoringTransferSection';
import AlegraDraftReminderSection from '../../components/AlegraDraftReminderSection';
import InformeMensualOTsSection from '../../components/InformeMensualOTsSection';
import AlertaUtilidadNegativaSection from '../../components/AlertaUtilidadNegativaSection';
import AlertaDteSinOtSection from '../../components/AlertaDteSinOtSection';
import RequisicionesPendientesSection from '../../components/RequisicionesPendientesSection';
import {
  BellRing, Mail, Plus, X, Send, Save, FileSpreadsheet, Clock, Users,
  CheckCircle2, AlertCircle, History,
} from 'lucide-react';
import { toast } from 'sonner';

const DIAS_SEMANA = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const formatCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

/** Lista editable de chips de email — agregar/quitar. */
function EmailListInput({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error('Email inválido');
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map(e => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function RecordatoriosPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, h] = await Promise.all([
        api.get('/recordatorios/oc-sin-doc/config'),
        api.get('/recordatorios/oc-sin-doc/preview'),
        api.get('/recordatorios/oc-sin-doc/log?limit=10'),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/recordatorios/oc-sin-doc/config', cfg);
      setCfg(res.data);
      toast.success('Configuración guardada');
      // Recargar preview para reflejar nuevo dias_min
      api.get('/recordatorios/oc-sin-doc/preview').then(r => setPreview(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!cfg.to || cfg.to.length === 0) {
      toast.error('Agrega al menos un destinatario antes de enviar');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/recordatorios/oc-sin-doc/send-now');
      const d = res.data;
      if (d.sent) {
        toast.success(`Email enviado a ${d.to.length} destinatario(s) con ${d.count} OC(s)`);
      } else {
        toast.info(`No enviado: ${d.reason || d.error || 'sin items'}`);
      }
      api.get('/recordatorios/oc-sin-doc/log?limit=10').then(r => setHistory(r.data?.items || []));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando');
    } finally {
      setSending(false);
    }
  };

  if (loading || !cfg) {
    return <div className="p-6 space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BellRing className="w-6 h-6 text-blue-600" /> Recordatorios Automáticos
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configura emails periódicos al equipo de compras y otros interesados.
        </p>
      </div>

      <Card data-testid="card-recordatorio-oc-sin-doc">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" /> OCs sin documento físico recibido
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Email semanal con OCs aprobadas hace ≥ N días que aún no tienen factura, guía o boleta registrada.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="switch-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Configuración */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Antigüedad mínima (días)
              </label>
              <Input
                type="number" min={1} max={90}
                value={cfg.dias_min}
                onChange={(e) => setCfg({ ...cfg, dias_min: parseInt(e.target.value, 10) || 7 })}
                data-testid="input-dias-min"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">OCs aprobadas hace ≥ X días sin factura.</p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Día de envío (semanal)</label>
              <select
                value={cfg.dia_semana}
                onChange={(e) => setCfg({ ...cfg, dia_semana: parseInt(e.target.value, 10) })}
                className="w-full h-9 px-3 rounded-md border border-input text-sm"
                data-testid="select-dia-semana"
              >
                {DIAS_SEMANA.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Hora (24h)</label>
              <Input
                type="time"
                value={cfg.hora}
                onChange={(e) => setCfg({ ...cfg, hora: e.target.value })}
                data-testid="input-hora"
              />
            </div>
          </div>

          {/* Destinatarios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
            <EmailListInput
              label="Destinatarios principales (Para)"
              items={cfg.to}
              onChange={(t) => setCfg({ ...cfg, to: t })}
              placeholder="email@chirinosteel.com"
              testId="to"
            />
            <EmailListInput
              label="Copia (CC)"
              items={cfg.cc}
              onChange={(c) => setCfg({ ...cfg, cc: c })}
              placeholder="copia@chirinosteel.com"
              testId="cc"
            />
          </div>

          {/* Acciones */}
          <div className="flex flex-wrap gap-2 pt-3 border-t">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-cfg">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
            <Button onClick={handleSendNow} disabled={sending || !cfg.to.length} variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="btn-send-now">
              <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Enviando…' : 'Enviar ahora (test)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview de OCs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet className="w-4 h-4" /> Vista previa: {preview?.count || 0} OCs incluidas
            </CardTitle>
            <span className="text-xs text-muted-foreground">
              Total agregado: <strong className="text-blue-700">{formatCLP(preview?.total || 0)}</strong>
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">N° OC</th>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Proveedor</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-center px-3 py-2">Días</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.items || []).slice(0, 50).map((i, idx) => (
                  <tr key={idx} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-1.5 font-mono font-semibold">OC-{i.numero_oc}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{i.fecha}</td>
                    <td className="px-3 py-1.5 truncate max-w-xs">{i.proveedor || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{formatCLP(i.total)}</td>
                    <td className="px-3 py-1.5 text-center text-amber-700 font-bold">{i.dias_antiguedad}d</td>
                  </tr>
                ))}
                {(preview?.items?.length || 0) === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                    No hay OCs pendientes de documento. 🎉
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {(preview?.count || 0) > 50 && (
            <div className="p-2 text-[11px] text-muted-foreground text-center bg-muted/30">
              Mostrando 50 de {preview.count} — el email los incluye todos.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sección: Solicitud Transferencias Factoring */}
      <FactoringTransferSection />

      {/* Iter 291.k (Carlos) — Recordatorio diario de facturas Alegra atascadas en DRAFT */}
      <AlegraDraftReminderSection />

      {/* Iter 294.c (Carlos) — Informe Excel mensual de OTs en ejecución a Gerencia */}
      <InformeMensualOTsSection />

      {/* Iter 294.d (Carlos) — Alerta inmediata de utilidad negativa en OTs en ejecución */}
      <AlertaUtilidadNegativaSection />

      {/* Iter 304 — Alerta diaria de DTEs de venta sin OT asignada */}
      <AlertaDteSinOtSection />

      {/* Iter 308.b (Renzo, feb-2026) — Recordatorio Requisiciones pendientes de aprobación */}
      <RequisicionesPendientesSection />

      {/* Historial */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><History className="w-4 h-4" /> Historial de envíos</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">Aún no se han enviado emails.</div>
          ) : (
            <div className="divide-y">
              {history.map((h, idx) => (
                <div key={idx} className="px-4 py-2.5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    {h.success ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                    <span>{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                    <Badge variant="outline" className="text-[10px]">{h.manual ? 'manual' : 'auto'}</Badge>
                    <span className="text-muted-foreground">→ {(h.to || []).join(', ')}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {h.items_count} OC(s) {h.error && <span className="text-red-600 ml-2">{h.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
